/**
 * GameOverModal.tsx — R14 newspaper-styled endgame stack
 *
 * Front-page framing wraps the score / narrative / restart stack. Sections:
 *   1. Masthead: "THE PLANETARY TIMES" + dateline (era, round, date)
 *   2. Kicker + headline (variant-driven) + subhead (narrative one-liner)
 *   3. Winner card (featured banner) — seat color, archetype, key stats
 *   4. SDG impact panel — global achievements counted from builtFacilities
 *   5. Narrative paragraph (existing reasonNarrative)
 *   6. Score table (per-seat tokens / energy / pollution / score)
 *   7. Footer stats (rounds, total pollution) + Restart button
 *
 * Visual contract: newspaper serif font, parchment palette, double-rule
 * border, kicker-style category caps in maroon. Replaces the prior dark
 * monospace modal (R9 vintage) entirely.
 *
 * VISIBLE WHEN: `gameStatus === "ended"`.
 */

import type React from "react";
import { useEffect, useMemo } from "react";
import { bgmKeyForEndReason, crossfadeBGM, playSfx } from "../../audio/audio.js";
import { useI18n } from "../../i18n/useI18n.js";
import { FACILITIES } from "../../store/facilities.js";
import { seatsInGame } from "../../store/game-end.js";
import { SEAT_COLORS, freshGameSeed, seatSymbolFor, useGameStore } from "../../store/game-store.js";
import type { SeatIndex } from "../../store/game-store.js";
import styles from "./GameOverModal.module.css";

const SEAT_NAMES = ["Player 1", "Player 2", "Player 3", "Player 4"] as const;

function seatName(seat: SeatIndex): string {
  return SEAT_NAMES[seat] ?? `P${seat + 1}`;
}

// ---------------------------------------------------------------------------
// Variant style: kicker, headline, subhead — derived from endReason
// ---------------------------------------------------------------------------

interface VariantStyle {
  kicker: string;
  headline: string;
  subhead: string;
}

function variantFor(reason: ReturnType<typeof useGameStore.getState>["endReason"]): VariantStyle {
  switch (reason) {
    case "bankruptcy-solo-survivor":
    case "economic-hegemony":
      return {
        kicker: "Special Edition · Economic Report",
        headline: "VICTORY: LAST EMPIRE STANDING",
        subhead: "A single empire endures while rivals collapse",
      };
    case "tipping-point":
    case "global-warming":
      return {
        kicker: "Breaking · Climate Catastrophe",
        headline: "TIPPING POINT REACHED",
        subhead: "Cumulative pollution crosses the planetary threshold",
      };
    case "mutual-destruction":
      return {
        kicker: "Obituary · All-Empire Collapse",
        headline: "MUTUAL DESTRUCTION",
        subhead: "Unchecked competition leaves no empire standing",
      };
    case "renewable-pathway":
    case "green-future":
      return {
        kicker: "Front Page · Renewable Transition",
        headline: "RENEWABLE PATHWAY ACHIEVED",
        subhead: "Cooperative shift to clean energy succeeds",
      };
    case "round-limit":
      return {
        kicker: "Era IV · Final Accounting",
        headline: "ERA IV CLOSES",
        subhead: "Thirty rounds end with a top-scoring civilization crowned",
      };
    default:
      return {
        kicker: "Final Edition",
        headline: "GAME OVER",
        subhead: "",
      };
  }
}

function reasonNarrative(
  reason: ReturnType<typeof useGameStore.getState>["endReason"],
  winnerSeat: SeatIndex | null,
  winnerSeats: ReadonlyArray<SeatIndex>,
  lostSeats: ReadonlySet<SeatIndex>,
): string {
  switch (reason) {
    case "bankruptcy-solo-survivor": {
      const lost = [...lostSeats].sort();
      const lostNames = lost.map((s) => seatName(s)).join(", ");
      const winner = winnerSeat !== null ? seatName(winnerSeat) : "?";
      return `${lostNames} ${lost.length === 1 ? "has" : "have"} gone bankrupt. ${winner} is the last empire standing and claims victory through economic dominance.`;
    }
    case "economic-hegemony": {
      const winner = winnerSeat !== null ? seatName(winnerSeat) : "?";
      return `${winner} holds economic hegemony: more than double the nearest rival's tokens and control of 35%+ of all built facilities. The economy bows to one power.`;
    }
    case "tipping-point":
    case "global-warming":
      return "Cumulative pollution crossed 600 units — the planetary tipping point. Climate feedback loops are now irreversible. All players lose; no empire survives the collapse.";
    case "mutual-destruction":
      return "Every empire has gone bankrupt. Unchecked competition destroyed all four economies. The planet is scarred and leaderless.";
    case "renewable-pathway":
    case "green-future": {
      const winnerNames = winnerSeats.map((s) => seatName(s)).join(", ");
      return `${winnerNames} achieved a cooperative renewable transition: ≥60% of built facilities are Renewable, total pollution below 300, by round 18. The future is green and shared.`;
    }
    case "round-limit": {
      const winner = winnerSeat !== null ? seatName(winnerSeat) : "?";
      return `30 rounds have passed. The final accounting (tokens + renewables×10 + energy×0.5 − pollution×1.5) ranks ${winner} as the highest-scoring civilization.`;
    }
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Score row (existing, unchanged behavior)
// ---------------------------------------------------------------------------

interface ScoreRowProps {
  seat: SeatIndex;
  tokens: number;
  energy: number;
  pollution: number;
  bankrupt: boolean;
  isWinner: boolean;
}

function ScoreRow({
  seat,
  tokens,
  energy,
  pollution,
  bankrupt,
  isWinner,
}: ScoreRowProps): React.ReactElement {
  const score = tokens + energy - pollution;
  return (
    <tr
      className={isWinner ? (styles.winnerScoreRow ?? "") : ""}
      data-testid={`gom-score-row-${seat}`}
    >
      <td>
        {/* R15 Builder-AW — colorblind symbol prefix in score table */}
        <span aria-hidden="true" style={{ color: SEAT_COLORS[seat], marginRight: 4 }}>
          {seatSymbolFor(seat)}
        </span>
        {seatName(seat)}
      </td>
      <td>{bankrupt ? "—" : tokens}</td>
      <td>{bankrupt ? "—" : energy}</td>
      <td>{bankrupt ? "—" : pollution}</td>
      <td>{bankrupt ? "BANKRUPT" : score}</td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// SDG stats computation
//
// Maps facility category → SDG impact tag. Counted globally across all seats.
// ---------------------------------------------------------------------------

interface SdgStats {
  renewablesBuilt: number; // SDG 7
  fossilsBuilt: number; // negative SDG 13
  nuclearBuilt: number; // SDG 7 (debated)
  wasteBuilt: number; // SDG 12
  geothermalBuilt: number; // SDG 7 + 13
  totalBuilt: number;
  renewableRatio: number;
  policiesEnacted: number; // SDG 16 (institutions) approximation: spentPolicyIds[]
}

function computeSdgStats(
  builtFacilities: Record<number, string | undefined>,
  spentPolicyIds: ReadonlyArray<string>,
): SdgStats {
  let renewables = 0;
  let fossils = 0;
  let nuclear = 0;
  let waste = 0;
  let geothermal = 0;
  let total = 0;
  for (const fid of Object.values(builtFacilities)) {
    if (fid === undefined) continue;
    total++;
    const def = FACILITIES.find((f) => f.id === fid);
    if (def === undefined) continue;
    switch (def.category) {
      case "Renewable":
        renewables++;
        break;
      case "Fossil":
        fossils++;
        break;
      case "Nuclear":
        nuclear++;
        break;
      case "Waste":
        waste++;
        break;
      case "Geothermal":
        geothermal++;
        break;
    }
  }
  return {
    renewablesBuilt: renewables,
    fossilsBuilt: fossils,
    nuclearBuilt: nuclear,
    wasteBuilt: waste,
    geothermalBuilt: geothermal,
    totalBuilt: total,
    renewableRatio: total > 0 ? Math.round((renewables / total) * 100) : 0,
    policiesEnacted: spentPolicyIds.length,
  };
}

interface SdgItemProps {
  tag: string;
  value: string;
  label: string;
  testid: string;
}

function SdgItem({ tag, value, label, testid }: SdgItemProps): React.ReactElement {
  return (
    <div className={styles.sdgItem} data-testid={testid}>
      <div className={styles.sdgItemTag}>{tag}</div>
      <p className={styles.sdgItemValue}>{value}</p>
      <div className={styles.sdgItemLabel}>{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Winner card
// ---------------------------------------------------------------------------

const ARCHETYPE_TITLE: Record<string, string> = {
  engineer: "Engineer",
  scientist: "Scientist",
  activist: "Activist",
  politician: "Politician",
};

interface WinnerCardProps {
  winnerSeat: SeatIndex;
  archetype: string;
  tokens: number;
  energy: number;
  pollution: number;
  renewablesOwned: number;
  facilitiesOwned: number;
  t: (key: string) => string;
}

function WinnerCard(props: WinnerCardProps): React.ReactElement {
  const seatColor = SEAT_COLORS[props.winnerSeat] ?? "#888";
  const archetypeTitle = ARCHETYPE_TITLE[props.archetype] ?? props.archetype;
  return (
    <div className={styles.winnerCard} data-testid="gom-winner-card">
      <div className={styles.winnerCardSeatBlock}>
        <div className={styles.winnerCardSeatPip} style={{ backgroundColor: seatColor }} />
        {/* R15 Builder-AW — colorblind seat symbol next to pip */}
        <span aria-hidden="true" style={{ color: seatColor, fontSize: "0.9em" }}>
          {seatSymbolFor(props.winnerSeat)}
        </span>
        <div className={styles.winnerCardSeatLabel} style={{ color: seatColor }}>
          {seatName(props.winnerSeat)}
        </div>
      </div>
      <div className={styles.winnerCardBody}>
        <div className={styles.winnerCardKicker}>Most Valuable Empire</div>
        <div className={styles.winnerCardName}>{seatName(props.winnerSeat)}</div>
        <div className={styles.winnerCardArchetype}>The {archetypeTitle}</div>
        <div className={styles.winnerCardStats}>
          <span>
            <b>{props.t("gameOver.winnerCard.tokens")}</b> {props.tokens}
          </span>
          <span>
            <b>{props.t("gameOver.winnerCard.energy")}</b> {props.energy}
          </span>
          <span>
            <b>{props.t("gameOver.winnerCard.pollution")}</b> {props.pollution}
          </span>
          <span>
            <b>{props.t("gameOver.winnerCard.facilities")}</b> {props.facilitiesOwned}
          </span>
          <span>
            <b>{props.t("gameOver.winnerCard.renewables")}</b> {props.renewablesOwned}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function GameOverModal(): React.ReactElement | null {
  const { t } = useI18n();
  const gameStatus = useGameStore((s) => s.gameStatus);
  const endReason = useGameStore((s) => s.endReason);
  const winnerSeat = useGameStore((s) => s.winnerSeat);
  const winnerSeats = useGameStore((s) => s.winnerSeats);
  const lostSeats = useGameStore((s) => s.lostSeats);
  const tokens = useGameStore((s) => s.tokens);
  const energy = useGameStore((s) => s.energy);
  const pollution = useGameStore((s) => s.pollution);
  const round = useGameStore((s) => s.round);
  const era = useGameStore((s) => s.era);
  const builtFacilities = useGameStore((s) => s.builtFacilities);
  const ownedBy = useGameStore((s) => s.ownedBy);
  const playerPerks = useGameStore((s) => s.playerPerks);
  const spentPolicyIds = useGameStore((s) => s.spentPolicyIds);
  const resetGame = useGameStore((s) => s.resetGame);
  const audioMuted = useGameStore((s) => s.audioMuted);
  const numPlayers = useGameStore((s) => s.numPlayers);

  // BGM crossfade by ending bucket (unchanged from R10.5).
  useEffect(() => {
    if (gameStatus !== "ended") return;
    const isDefeat =
      endReason === "tipping-point" ||
      endReason === "global-warming" ||
      endReason === "mutual-destruction";
    if (isDefeat) playSfx("bankruptcy");
    else playSfx("era-transition");
    if (!audioMuted && endReason) crossfadeBGM(bgmKeyForEndReason(endReason), 1200);
  }, [gameStatus, endReason, audioMuted]);

  // Always compute SDG stats unconditionally so hook order is stable.
  const sdgStats = useMemo(
    () => computeSdgStats(builtFacilities, spentPolicyIds),
    [builtFacilities, spentPolicyIds],
  );

  if (gameStatus !== "ended") return null;

  const variant = variantFor(endReason);
  const narrative = reasonNarrative(endReason, winnerSeat, winnerSeats, lostSeats);
  const totalPollution = pollution[0] + pollution[1] + pollution[2] + pollution[3];
  // Tipping point threshold — keep in sync with game-end.ts TIPPING_POINT_THRESHOLD (600).
  const winnerSet = new Set<SeatIndex>(winnerSeats);

  // Pick a "featured winner" for the WinnerCard — winnerSeat preferred, else
  // first winnerSeats entry. None if both empty (e.g. mutual destruction).
  const featuredWinner: SeatIndex | null = winnerSeat ?? winnerSeats[0] ?? null;

  // Per-seat renewable + facility counts for winner card.
  // R14 Codex-B MED: flag orphan facilities (no owner) and unknown facility ids
  // in dev — they indicate corrupted board state and silently skipping them in
  // the endgame stats panel would mask the bug.
  const renewablesOwnedBy: Record<SeatIndex, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
  const facilitiesOwnedBy: Record<SeatIndex, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
  for (const [tileIdxStr, fid] of Object.entries(builtFacilities)) {
    if (fid === undefined) continue;
    const owner = ownedBy[Number(tileIdxStr)] as SeatIndex | undefined;
    if (owner === undefined) {
      if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV === true) {
        console.warn(`[GameOverModal] orphan built facility at tile ${tileIdxStr} (${fid})`);
      }
      continue;
    }
    facilitiesOwnedBy[owner]++;
    const def = FACILITIES.find((f) => f.id === fid);
    if (def === undefined) {
      if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV === true) {
        console.warn(`[GameOverModal] unknown facility id "${fid}" at tile ${tileIdxStr}`);
      }
      continue;
    }
    if (def.category === "Renewable") renewablesOwnedBy[owner]++;
  }

  return (
    <dialog open className={styles.overlay} data-testid="gom-overlay" aria-labelledby="gom-title">
      <div className={styles.newspaperFrame} data-testid="gom-newspaper" data-light-surface="true">
        <header className={styles.masthead} data-testid="gom-masthead">
          <span className={styles.mastheadTitle}>The Planetary Times</span>
          <span className={styles.mastheadDate}>
            Era {era} · Round {round} · 2026
          </span>
        </header>

        <div className={styles.kicker} data-testid="gom-kicker">
          {variant.kicker}
        </div>
        <h1
          id="gom-title"
          className={styles.npHeadline}
          data-testid="gom-title"
          data-end-reason={endReason ?? "none"}
        >
          {variant.headline}
        </h1>
        <p className={styles.npSubhead} data-testid="gom-subhead">
          {variant.subhead}
        </p>
        <p className={styles.npByline}>Filed from the Council of Empires, Era {era}</p>

        <hr className={styles.npDivider} />

        {featuredWinner !== null && (
          <WinnerCard
            winnerSeat={featuredWinner}
            archetype={playerPerks[featuredWinner]}
            tokens={tokens[featuredWinner] ?? 0}
            energy={energy[featuredWinner] ?? 0}
            pollution={pollution[featuredWinner] ?? 0}
            renewablesOwned={renewablesOwnedBy[featuredWinner]}
            facilitiesOwned={facilitiesOwnedBy[featuredWinner]}
            t={t}
          />
        )}

        <section className={styles.sdgPanel} data-testid="gom-sdg-panel">
          <div className={styles.sdgPanelHeader}>SDG Impact Report · Global</div>
          <div className={styles.sdgGrid}>
            <SdgItem
              tag="SDG 7"
              value={String(sdgStats.renewablesBuilt)}
              label="Renewables built"
              testid="gom-sdg-renewables"
            />
            <SdgItem
              tag="SDG 13"
              value={`${sdgStats.renewableRatio}%`}
              label="Clean-energy share"
              testid="gom-sdg-clean-share"
            />
            <SdgItem
              tag="SDG 12"
              value={String(sdgStats.wasteBuilt)}
              label="Waste-to-energy plants"
              testid="gom-sdg-waste"
            />
            <SdgItem
              tag="SDG 13"
              value={String(sdgStats.fossilsBuilt)}
              label="Fossil legacy plants"
              testid="gom-sdg-fossils"
            />
            <SdgItem
              tag="SDG 16"
              value={String(sdgStats.policiesEnacted)}
              label="Policies drawn"
              testid="gom-sdg-policies"
            />
            <SdgItem
              tag="POLLUTION"
              value={`${totalPollution}/600`}
              label="Total accumulated"
              testid="gom-sdg-pollution"
            />
          </div>
        </section>

        <div className={styles.npBodyTable}>
          <p className={styles.npReason}>{narrative}</p>

          <table className={styles.npScoreTable} data-testid="gom-score-table">
            <thead>
              <tr>
                <th>Seat</th>
                <th>{t("gameOver.scoreTable.tokens")}</th>
                <th>{t("gameOver.scoreTable.energy")}</th>
                <th>{t("gameOver.scoreTable.pollution")}</th>
                <th>{t("gameOver.scoreTable.score")}</th>
              </tr>
            </thead>
            <tbody>
              {/* Present seats only (< numPlayers). Absent seats keep INITIAL state
                  (100T, 0 pollution) and must not appear in the final scoreboard. */}
              {seatsInGame(numPlayers).map((seat) => (
                <ScoreRow
                  key={seat}
                  seat={seat}
                  tokens={tokens[seat] ?? 0}
                  energy={energy[seat] ?? 0}
                  pollution={pollution[seat] ?? 0}
                  bankrupt={lostSeats.has(seat)}
                  isWinner={winnerSet.has(seat)}
                />
              ))}
            </tbody>
          </table>

          <div className={styles.npStatsRow}>
            <span>Rounds played: {round}</span>
            <span>Total facilities: {sdgStats.totalBuilt}</span>
            <span>Pollution: {totalPollution} / 600</span>
          </div>
        </div>

        <div className={styles.npFooter}>
          {/* R15 Builder-AW: autoFocus so keyboard users land on the only action. */}
          <button
            type="button"
            className={styles.npRestartBtn}
            onClick={() => resetGame(freshGameSeed())}
            data-testid="gom-restart"
            // biome-ignore lint/a11y/noAutofocus: game-over modal is a terminal state; focus must land on the single action button so keyboard/AT users can proceed without Tab
            autoFocus
          >
            ▶ NEW GAME
          </button>
        </div>
      </div>
    </dialog>
  );
}
